import { useMemo } from 'react';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { PERMISSIONS } from '@/types/permissions';

export const usePermissions = () => {
  // Usar useAuthorization de forma defensiva para evitar quebra de componentes
  let authz;
  try {
    authz = useAuthorization();
  } catch (e) {
    console.error("❌ usePermissions: Erro ao obter contexto de autorização:", e);
    // Retornar um objeto de autorização vazio/carregando
    authz = {
      can: () => false,
      canAccess: () => false,
      hasRole: () => false,
      userRole: null,
      userProfile: null,
      isLoading: true,
      error: "Contexto não encontrado",
      refreshUserProfile: async () => {},
      updateUserRole: async () => {},
      canManageOverrides: false,
      refreshOverrides: async () => {}
    };
  }

  const { can, canAccess, hasRole, userRole, userProfile, isLoading } = authz;
  
  return useMemo(() => {
    // Log apenas quando houver mudança real e necessária para debug
    // if (import.meta.env.DEV) {
    //   console.log('🔐 usePermissions hook recalculando:', {
    //     userRole,
    //     userProfile: userProfile?.nome,
    //     isLoading,
    //   });
    // }
    
    // Se ainda estiver carregando, retornar objeto vazio com funções seguras
    if (isLoading) {
      return {
        userRole: null,
        userProfile: null,
        can: () => false,
        canAccess: () => false,
        hasRole: () => false,
        leads: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        clients: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        contracts: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        proposals: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        users: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        reports: {
          canRead: () => false,
          canExport: () => false,
          canManage: () => false,
        },
        settings: {
          canRead: () => false,
          canUpdate: () => false,
          canManage: () => false,
        },
        dimensioning: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
        },
        distributors: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canAccess: () => false,
        },
        parceiros: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canViewMetrics: () => false,
          canAccess: () => false,
        },
        vendedores: {
          canManage: () => false,
          canCreate: () => false,
          canRead: () => false,
          canUpdate: () => false,
          canDelete: () => false,
          canViewMetrics: () => false,
          canAccess: () => false,
        },
        comissoes: {
          canRead: () => false,
          canManage: () => false,
        },
        isAdmin: () => false,
        isRepresentative: () => false,
        isPartner: () => false,
        canViewDashboard: () => false,
        canManagePipeline: () => false,
        canViewFinancial: () => false,
        canManageTerritories: () => false,
        canViewMetrics: () => false,
      };
    }

    // Verificações de permissões específicas
    const canManageLeads = () => can(PERMISSIONS.LEADS.MANAGE);
    const canCreateLeads = () => can(PERMISSIONS.LEADS.CREATE);
    const canReadLeads = () => can(PERMISSIONS.LEADS.READ);
    const canUpdateLeads = () => can(PERMISSIONS.LEADS.UPDATE);
    const canDeleteLeads = () => can(PERMISSIONS.LEADS.DELETE);

    const canManageClients = () => can(PERMISSIONS.CLIENTS.MANAGE);
    const canCreateClients = () => can(PERMISSIONS.CLIENTS.CREATE);
    const canReadClients = () => can(PERMISSIONS.CLIENTS.READ);
    const canUpdateClients = () => can(PERMISSIONS.CLIENTS.UPDATE);
    const canDeleteClients = () => can(PERMISSIONS.CLIENTS.DELETE);

    const canManageContracts = () => can(PERMISSIONS.CONTRACTS.MANAGE);
    const canCreateContracts = () => can(PERMISSIONS.CONTRACTS.CREATE);
    const canReadContracts = () => can(PERMISSIONS.CONTRACTS.READ);
    const canUpdateContracts = () => can(PERMISSIONS.CONTRACTS.UPDATE);
    const canDeleteContracts = () => can(PERMISSIONS.CONTRACTS.DELETE);

    const canManageProposals = () => can(PERMISSIONS.PROPOSALS.MANAGE);
    const canCreateProposals = () => can(PERMISSIONS.PROPOSALS.CREATE);
    const canReadProposals = () => can(PERMISSIONS.PROPOSALS.READ);
    const canUpdateProposals = () => can(PERMISSIONS.PROPOSALS.UPDATE);
    const canDeleteProposals = () => can(PERMISSIONS.PROPOSALS.DELETE);

    const canManageUsers = () => can(PERMISSIONS.USERS.MANAGE);
    const canCreateUsers = () => can(PERMISSIONS.USERS.CREATE);
    const canReadUsers = () => can(PERMISSIONS.USERS.READ);
    const canUpdateUsers = () => can(PERMISSIONS.USERS.UPDATE);
    const canDeleteUsers = () => can(PERMISSIONS.USERS.DELETE);

    const canReadReports = () => can(PERMISSIONS.REPORTS.READ);
    const canExportReports = () => can(PERMISSIONS.REPORTS.EXPORT);
    const canManageReports = () => can(PERMISSIONS.REPORTS.MANAGE);

    const canReadSettings = () => can(PERMISSIONS.SETTINGS.READ);
    const canUpdateSettings = () => can(PERMISSIONS.SETTINGS.UPDATE);
    const canManageSettings = () => can(PERMISSIONS.SETTINGS.MANAGE);

    const canManageDimensioning = () => can(PERMISSIONS.DIMENSIONING.MANAGE);
    const canCreateDimensioning = () => can(PERMISSIONS.DIMENSIONING.CREATE);
    const canReadDimensioning = () => can(PERMISSIONS.DIMENSIONING.READ);
    const canUpdateDimensioning = () => can(PERMISSIONS.DIMENSIONING.UPDATE);
    const canDeleteDimensioning = () => can(PERMISSIONS.DIMENSIONING.DELETE);

    const canManageDistributors = () => can(PERMISSIONS.DISTRIBUTORS.MANAGE);
    const canCreateDistributors = () => can(PERMISSIONS.DISTRIBUTORS.CREATE);
    const canReadDistributors = () => can(PERMISSIONS.DISTRIBUTORS.READ);
    const canUpdateDistributors = () => can(PERMISSIONS.DISTRIBUTORS.UPDATE);
    const canDeleteDistributors = () => can(PERMISSIONS.DISTRIBUTORS.DELETE);

    const canManageVendedores = () => can(PERMISSIONS.VENDEDORES.MANAGE);
    const canCreateVendedores = () => can(PERMISSIONS.VENDEDORES.CREATE);
    const canReadVendedores = () => can(PERMISSIONS.VENDEDORES.READ);
    const canUpdateVendedores = () => can(PERMISSIONS.VENDEDORES.UPDATE);
    const canDeleteVendedores = () => can(PERMISSIONS.VENDEDORES.DELETE);
    const canViewMetricsVendedores = () => can(PERMISSIONS.VENDEDORES.VIEW_METRICS);
    const canAccessVendedores = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('vendedores', action);

    // Verificações de perfil
    const isAdmin = () => hasRole('admin');
    const isRepresentative = () => hasRole('representante');
    const isPartner = () => hasRole('parceiro');

    // Verificações de acesso a recursos
    const canAccessLeads = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('leads', action);
    
    const canAccessClients = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('clients', action);
    
    const canAccessContracts = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('contracts', action);
    
    const canAccessProposals = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('proposals', action);
    
    const canAccessUsers = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('users', action);

    const canAccessDistributors = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('distributors', action);

    // Verificações de permissões de Parceiros
    const canManageParceiros = () => can(PERMISSIONS.PARCEIROS.MANAGE);
    const canCreateParceiros = () => can(PERMISSIONS.PARCEIROS.CREATE);
    const canReadParceiros = () => can(PERMISSIONS.PARCEIROS.READ);
    const canUpdateParceiros = () => can(PERMISSIONS.PARCEIROS.UPDATE);
    const canDeleteParceiros = () => can(PERMISSIONS.PARCEIROS.DELETE);
    const canViewMetricsParceiros = () => can(PERMISSIONS.PARCEIROS.VIEW_METRICS);
    const canAccessParceiros = (action: 'create' | 'read' | 'update' | 'delete') => 
      canAccess('parceiros', action);

    const canReadComissoes = () => can(PERMISSIONS.COMISSOES.READ);
    const canManageComissoes = () => can(PERMISSIONS.COMISSOES.MANAGE);

    // Verificações de funcionalidades específicas
    const canViewDashboard = () => canReadLeads() || canReadClients() || canReadProposals();
    const canManagePipeline = () => canManageLeads() || canManageProposals();
    const canViewFinancial = () => canReadContracts() || canReadProposals();
    const canManageTerritories = () => isAdmin();
    const canViewMetrics = () => canReadReports() || isAdmin() || userRole === 'vendedor';

    return {
      // Estado do usuário
      userRole,
      userProfile,
      
      // Verificações de permissões
      can,
      canAccess,
      hasRole,
      
      // Permissões específicas por módulo
      leads: {
        canManage: canManageLeads,
        canCreate: canCreateLeads,
        canRead: canReadLeads,
        canUpdate: canUpdateLeads,
        canDelete: canDeleteLeads,
        canAccess: canAccessLeads,
      },
      
      clients: {
        canManage: canManageClients,
        canCreate: canCreateClients,
        canRead: canReadClients,
        canUpdate: canUpdateClients,
        canDelete: canDeleteClients,
        canAccess: canAccessClients,
      },
      
      contracts: {
        canManage: canManageContracts,
        canCreate: canCreateContracts,
        canRead: canReadContracts,
        canUpdate: canUpdateContracts,
        canDelete: canDeleteContracts,
        canAccess: canAccessContracts,
      },
      
      proposals: {
        canManage: canManageProposals,
        canCreate: canCreateProposals,
        canRead: canReadProposals,
        canUpdate: canUpdateProposals,
        canDelete: canDeleteProposals,
        canAccess: canAccessProposals,
      },
      
      users: {
        canManage: canManageUsers,
        canCreate: canCreateUsers,
        canRead: canReadUsers,
        canUpdate: canUpdateUsers,
        canDelete: canDeleteUsers,
        canAccess: canAccessUsers,
      },
      
      reports: {
        canRead: canReadReports,
        canExport: canExportReports,
        canManage: canManageReports,
      },
      
      settings: {
        canRead: canReadSettings,
        canUpdate: canUpdateSettings,
        canManage: canManageSettings,
      },
      
      dimensioning: {
        canManage: canManageDimensioning,
        canCreate: canCreateDimensioning,
        canRead: canReadDimensioning,
        canUpdate: canUpdateDimensioning,
        canDelete: canDeleteDimensioning,
      },
      
      distributors: {
        canManage: canManageDistributors,
        canCreate: canCreateDistributors,
        canRead: canReadDistributors,
        canUpdate: canUpdateDistributors,
        canDelete: canDeleteDistributors,
        canAccess: canAccessDistributors,
      },
      
      parceiros: {
        canManage: canManageParceiros,
        canCreate: canCreateParceiros,
        canRead: canReadParceiros,
        canUpdate: canUpdateParceiros,
        canDelete: canDeleteParceiros,
        canViewMetrics: canViewMetricsParceiros,
        canAccess: canAccessParceiros,
      },
      
      vendedores: {
        canManage: canManageVendedores,
        canCreate: canCreateVendedores,
        canRead: canReadVendedores,
        canUpdate: canUpdateVendedores,
        canDelete: canDeleteVendedores,
        canViewMetrics: canViewMetricsVendedores,
        canAccess: canAccessVendedores,
      },

      comissoes: {
        canRead: canReadComissoes,
        canManage: canManageComissoes,
      },
      
      // Verificações de perfil
      isAdmin,
      isRepresentative,
      isPartner,
      
      // Verificações de funcionalidades
      canViewDashboard,
      canManagePipeline,
      canViewFinancial,
      canManageTerritories,
      canViewMetrics,
    };
  }, [can, canAccess, hasRole, userRole, userProfile, isLoading]);
};

